import {
  Component,
  Output,
  EventEmitter,
  inject,
  computed,
  signal,
} from '@angular/core';
import { NgIf, CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { PageTitleService } from '../../../services/page-title.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { FleetStateService } from '../../../services/state/fleet-state.service';
import { ShoppingCartStateService } from '../../../services/state/shopping-cart-state.service';

@Component({
  selector: 'app-topbar',
  imports: [CommonModule, NgIf, RouterLink],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
  standalone: true,
})
export class TopbarComponent {
  private pageTitleService = inject(PageTitleService);
  private auth = inject(AuthService);
  private fleetState = inject(FleetStateService);
  private cartState = inject(ShoppingCartStateService);
  private router = inject(Router);

  @Output() search = new EventEmitter<string>();
  @Output() newAudit = new EventEmitter<void>();

  get pageTitle(): string {
    return this.pageTitleService.pageTitle();
  }

  onSearch(query: string): void {
    this.search.emit(query);
  }

  onNewAudit(): void {
    this.newAudit.emit();
  }

  get isAuthenticated$() {
    return this.auth.isAuthenticated$;
  }

  get user$() {
    return this.auth.user$;
  }

  logout(): void {
    this.auth.logout();
  }

  activeFleetName = computed(
    () =>
      this.fleetState.activeFleet()?.label ||
      this.fleetState.activeFleet()?.['_id'] ||
      null
  );

  // Shopping cart
  cartItemCount = computed(() => this.cartState.itemCount());

  // Fleet selector dropdown state
  showFleetSelector = signal(false);
  fleetFilter = signal('');
  filteredFleets = computed(() => {
    const q = this.fleetFilter().toLowerCase().trim();
    const list = this.fleetState.fleets();
    if (!q) return list;
    return list.filter((f) => (f.label || f._id).toLowerCase().includes(q));
  });

  constructor() {
    // Ensure fleets are loaded once when topbar mounts
    this.fleetState.ensureLoaded();

    // Hide fleet selector on routes that aren't about a Fleet: onboarding,
    // and the User's own Account page
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const notFleetScoped =
          e.url.includes('/onboarding') || e.url.startsWith('/account');
        this._hideFleetSelector.set(notFleetScoped);
        if (notFleetScoped) this.showFleetSelector.set(false);
      });
  }

  toggleFleetSelector(): void {
    const next = !this.showFleetSelector();
    this.showFleetSelector.set(next);
    if (next) this.fleetFilter.set('');
  }

  onFleetFilterInput(value: string): void {
    this.fleetFilter.set(value);
  }

  selectFleet(id: string): void {
    this.fleetState.setActiveFleet(id);
    this.showFleetSelector.set(false);
  }

  isActive(id: string): boolean {
    return this.fleetState.activeFleetId() === id;
  }

  // Whether to hide the fleet selector (e.g., onboarding, account)
  private _hideFleetSelector = signal(false);
  hideFleetSelector() {
    return this._hideFleetSelector();
  }
}
